import type { PlaybackMode, VocalChannel } from '../../api/types.ts';

/**
 * The low-latency, same-machine sync path for AC-OPS-2 ("≤20ms ... using one shared clock over
 * BroadcastChannel, with the SSE channel as fallback"). `BroadcastChannel` delivers same-origin,
 * same-browser messages essentially synchronously (no network hop), which is what makes the 20ms
 * budget realistic — the SSE 'playback' channel (already wired in api/realtime.ts) is the durable,
 * cross-reload, cross-device fallback both windows also listen to independently.
 *
 * Kept deliberately dumb: this channel carries the CURRENT state, never a command history. A
 * window that missed a message (closed, reloaded, opened late) always has `GET /api/playback/state`
 * to hydrate from — see useStageWindow.ts / StageRoot.tsx.
 */

export interface StageSyncPayload {
  mode: PlaybackMode;
  isPlaying: boolean;
  positionMs: number;
  positionAtServerMs: number | null;
  keyShift: number;
  tempoPct: number;
  vocalChannel: VocalChannel;
  volume: number;
  lyricOffsetMs: number;
  seq: number;
  /** `song.documentId`, or null when idle/attract — the Stage window fetches the full song
   * record itself (poster/media URLs) rather than trusting a payload it did not authenticate. */
  songDocumentId: string | null;
  // R8-P1-3 fix (round 8, live-Chrome critic): without these, a same-song update delivered over
  // this channel (`usePlaybackState.ts`'s Path 1 merge) never touched the receiver's stale
  // `errorCode`/`errorMessage` — only a full `refresh()` (a song change, or a hard page reload)
  // ever picked up the server's cleared value, so the Stage's error hold card
  // (`stageScene.ts`'s `hasError` priority) could keep showing a stale "Couldn't play X" over a
  // song that was, by then, playing fine.
  errorCode: string | null;
  errorMessage: string | null;
}

export type StageMessage =
  | { type: 'state'; payload: StageSyncPayload; sentAtMs: number }
  | { type: 'announce'; text: string; durationSec: number; sentAtMs: number }
  | { type: 'fullscreen-request'; sentAtMs: number }
  | { type: 'lyrics-visibility'; visible: boolean; sentAtMs: number }
  // MVP-11 #8: the Stage window deliberately runs WITHOUT the shared SSE client (App.tsx stops it
  // off-console so no JWT-bearing connection lives in that window), so a settings write made in
  // the cockpit had no live path to it at all — only a reload. This is the same-machine nudge:
  // "settings changed, re-read them yourself" (no payload — the Stage fetches `GET /api/setting`
  // with its own credentials rather than trusting a broadcast body).
  | { type: 'settings-updated'; sentAtMs: number }
  | { type: 'ping'; sentAtMs: number }
  | { type: 'pong'; sentAtMs: number }
  // R12-P2-A fix (round 12, live-Chrome critic): a "still alive" beat the console posts every ~4s
  // purely so the Stage's poll-fallback clock (`playbackMath.ts`'s `shouldPollForFallback`) sees
  // recent traffic even while the cockpit is genuinely paused and has no `state` frame to send —
  // see `shouldPostHeartbeat`'s own doc comment. Carries no payload beyond a monotonic `seq` (kept
  // for parity/debugging only; nothing currently orders on it).
  | { type: 'heartbeat'; seq: number; sentAtMs: number };

const CHANNEL_NAME = 'mvcp-stage-v1';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Defensive parsing at the channel boundary — `BroadcastChannel` has no schema, and this same
 * origin could in principle host other scripts. A malformed or foreign message is just ignored. */
export function isStageMessage(data: unknown): data is StageMessage {
  if (!isRecord(data) || typeof data.type !== 'string') return false;
  switch (data.type) {
    case 'state':
      return isRecord(data.payload) && typeof data.sentAtMs === 'number';
    case 'announce':
      return typeof data.text === 'string' && typeof data.durationSec === 'number';
    case 'lyrics-visibility':
      return typeof data.visible === 'boolean' && typeof data.sentAtMs === 'number';
    case 'settings-updated':
    case 'fullscreen-request':
    case 'ping':
    case 'pong':
      return typeof data.sentAtMs === 'number';
    case 'heartbeat':
      return typeof data.seq === 'number' && typeof data.sentAtMs === 'number';
    default:
      return false;
  }
}

export interface StageChannelHandle {
  post(message: StageMessage): void;
  onMessage(listener: (message: StageMessage) => void): () => void;
  close(): void;
}

/** True in every real browser this product targets; guarded so a `node --test` run (no
 * `BroadcastChannel` global) can still import this module for its pure helpers above. */
export function isBroadcastChannelSupported(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

/** Opens (or no-ops) the shared channel. When `BroadcastChannel` is unavailable, returns a handle
 * that silently drops posts and never calls listeners — callers still work correctly because they
 * are required to also reconcile from the SSE-fed source of truth (AC-OPS-2's documented fallback),
 * never from this channel alone. */
export function openStageChannel(): StageChannelHandle {
  if (!isBroadcastChannelSupported()) {
    return { post: () => {}, onMessage: () => () => {}, close: () => {} };
  }
  const channel = new BroadcastChannel(CHANNEL_NAME);
  return {
    post(message) {
      channel.postMessage(message);
    },
    onMessage(listener) {
      const handler = (event: MessageEvent) => {
        if (isStageMessage(event.data)) listener(event.data);
      };
      channel.addEventListener('message', handler);
      return () => channel.removeEventListener('message', handler);
    },
    close() {
      channel.close();
    },
  };
}

/**
 * One instance per window/realm (mirrors `api/realtime.ts`'s `realtimeClient` singleton pattern).
 * Every ops-dualscreen consumer in this realm — the cockpit's transport actions, `usePlaybackState`,
 * `useStageWindow` — posts and listens through this SAME object, so a component unmounting never
 * tears down the channel out from under an unrelated listener.
 *
 * Deliberately lazy (created on first call, not a module-level `= openStageChannel()`): a live
 * `BroadcastChannel` keeps Node's event loop alive indefinitely (it is not `.unref()`-able), so an
 * eager singleton would hang every `node --test` run that so much as imports this module — the
 * whole reason `openStageChannel()`/`isBroadcastChannelSupported()` exist as a Node-safe factory in
 * the first place. Calling this getter is what actually opens the channel; a plain `node --test`
 * importing this file for its types/pure helpers never triggers it.
 */
let sharedChannel: StageChannelHandle | null = null;
export function getStageChannel(): StageChannelHandle {
  if (!sharedChannel) sharedChannel = openStageChannel();
  return sharedChannel;
}
